import React, { useState, useEffect, useCallback, useReducer, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { ScorecardPanel } from '../components/project/ScorecardPanel';
import { FinancialChart, SourcesUsesCalculator } from '../components/DealFinancials';
import { ExecutiveSummary } from '../components/ExecutiveSummary';
import { MeetingNotes } from '../components/MeetingNotes';
import { StageGateDialog, StageSuggestionBanner } from '../components/StageGateUI';
import { DealPhotosCard } from '../components/DealPhotosCard';
import { NdaCard } from '../components/NdaCard';
import { BrokerNdaReviewCard } from '../components/BrokerNdaReviewCard';
import { DeclineDialog } from '../components/BrokerInboxExtras';
import { AskOltaAI } from '../components/AskOltaAI';
import { DealContactsCard } from '../components/DealContactsCard';
import { DealLinkedContactsCard } from '../components/DealLinkedContactsCard';
import { DealSequencesCard } from '../components/DealSequencesCard';
import { EmailCorrespondenceCard } from '../components/EmailCorrespondenceCard';
import { DealTasks } from '../components/DealTasks';
import { OutreachStepsCard } from '../components/OutreachStepsCard';
import { DealTimeline } from '../components/DealTimeline';
import { DealLinks } from '../components/DealLinks';
import { DealActivityFeed } from './ActivityFeedPage';
import { DealComments } from '../components/DealComments';
import { DealACFeedback } from '../components/DealACFeedback';
import { DealOutreachTracking } from '../components/DealOutreachTracking';
import { ReferenceCallsCard } from '../components/ReferenceCallsCard';
import { AcKeyUpdatesCard } from '../components/AcKeyUpdatesCard';
import { ScreenDocButtons } from '../components/ScreenDocButtons';
import { ProcessSection, GroupChip } from '../components/ProcessSection';
import { CallGuideCard } from '../components/CallGuideCard';
import { OnsitePlannerCard } from '../components/OnsitePlannerCard';
import { PassReasonPicker, passReasonText } from '../components/PassReasonPicker';
import { PrioEditButton } from '../components/PrioEditDialog';
import { FinalApprovalDialog } from '../components/FinalApprovalDialog';
import { ImportCategoryChip } from '../components/ImportCategoryChip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { PrioCriteriaPicker, PrioBadge, computePrioWith, usePrioConfig, PRIO_LABEL } from '../components/PrioCriteriaPicker';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import {
  ArrowLeft, Play, Shield,
  CheckCircle, X, Loader2,
  Mail, Copy, Check, FileText, AlertCircle, MailX,
  CheckSquare, XCircle, Upload, ChevronDown, Globe, Download, PenTool, CalendarPlus, ExternalLink, MoreHorizontal, Activity, Star, Bookmark, Fish, Shuffle, Pencil, PhoneIncoming, UserPlus, Lightbulb, Undo2,
  BarChart3, Megaphone, Handshake, MessagesSquare, FolderOpen, MailWarning, RotateCcw,
} from 'lucide-react';

import { PIPELINE_STAGES, BROKERED_STAGES, STAGE_LABELS } from '../lib/stages';

// ISO → value for <input type="datetime-local"> in the browser's local time (Berlin for the team)
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};


export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isAcMember, user: currentUser } = useAuth();
  const { requestLeave } = useUnsavedChanges();
  const prioCfg = usePrioConfig();
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Single source of truth — all data lives here, not in child components
  const [project, setProject] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [viewingDocId, setViewingDocId] = useState(null);

  // Per-section screen status
  const [screenStatus, setScreenStatus] = useState({
    executive_summary: 'idle', financials: 'idle', phase1: 'idle', phase2: 'idle',
  });
  const pollingRef = useRef(null);

  // UI state
  const [editMode, setEditMode] = useState(null);
  const [editedScores, setEditedScores] = useState({});
  const [expandedCriteria, setExpandedCriteria] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [reviewingPhase, setReviewingPhase] = useState(null);
  const [foundDialog, setFoundDialog] = useState(null);
  const [foundNote, setFoundNote] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Force re-render counter
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // Document upload
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docLabel, setDocLabel] = useState('CIM');
  const [docUploading, setDocUploading] = useState(false);

  // Turn down dialog
  const [turnDownDialog, setTurnDownDialog] = useState(false);
  const [turnDownReason, setTurnDownReason] = useState('');
  const [turnDownTags, setTurnDownTags] = useState([]);
  const [turnDownBench, setTurnDownBench] = useState(false);
  const [turnDownStage, setTurnDownStage] = useState('');
  // Brokered pass → broker decline flow (sends the Absage mail, reason becomes the pass reason)
  const [brokerProposal, setBrokerProposal] = useState(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [passBrokerSkip, setPassBrokerSkip] = useState(false);

  // Team meeting hint
  const [hintDialog, setHintDialog] = useState(false);
  const [returnedDialog, setReturnedDialog] = useState(false);
  const [returnedNote, setReturnedNote] = useState('');
  const [returnedBusy, setReturnedBusy] = useState(false);
  const [stageBackOpen, setStageBackOpen] = useState(false);
  const [stageBackTarget, setStageBackTarget] = useState('');
  const [stageBackSaving, setStageBackSaving] = useState(false);
  const [hintText, setHintText] = useState('');
  const [savingHint, setSavingHint] = useState(false);

  // Team members for ownership
  const [teamMembers, setTeamMembers] = useState([]);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [gateInfo, setGateInfo] = useState(null);
  const [stageNote, setStageNote] = useState(null);
  const [finalApprovalOpen, setFinalApprovalOpen] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [claimWarn, setClaimWarn] = useState(null);
  const [namerOpen, setNamerOpen] = useState(false);
  const [namerCandidate, setNamerCandidate] = useState('');
  const [namerMeta, setNamerMeta] = useState({ topic: '', definition: '' });
  const [namerShuffling, setNamerShuffling] = useState(false);
  const [namerClaiming, setNamerClaiming] = useState(false);
  const warnedRef = useRef(null);
  const nudgeTimerRef = useRef(null);
  const decideTimerRef = useRef(null);
  const nudgeIdRef = useRef(null);
  const decideIdRef = useRef(null);

  // Claim flow: brokered deals get the broker's own project name, others a fishing-themed code name.
  const shuffleName = useCallback(async (useBroker = false) => {
    setNamerShuffling(true);
    try {
      const res = await api.generateProjectName(useBroker ? id : undefined);
      setNamerCandidate(res.data.random_project_name);
      setNamerMeta({ topic: res.data.topic || '', definition: res.data.definition || '' });
    } catch { toast.error('Could not generate a name'); }
    finally { setNamerShuffling(false); }
  }, [id]);

  const startClaim = useCallback(async () => {
    // Already named? Just (re)claim directly without the Namer popup.
    if (project?.random_project_name) {
      try { await api.claimDeal(id, currentUser.id, project.random_project_name); toast.success('Claimed'); await refresh(); }
      catch (err) { toast.error(err.response?.data?.detail || 'Claim failed'); }
      return;
    }
    setNamerCandidate('');
    setNamerMeta({ topic: '', definition: '' });
    setNamerOpen(true);
    shuffleName(true);
  }, [project, id, currentUser, shuffleName]);

  const confirmClaim = useCallback(async () => {
    if (!namerCandidate) return;
    setNamerClaiming(true);
    try {
      await api.claimDeal(id, currentUser.id, namerCandidate);
      toast.success(`Claimed as ${namerCandidate}`);
      setNamerOpen(false);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Claim failed'); }
    finally { setNamerClaiming(false); }
  }, [namerCandidate, id, currentUser]);


  // Load favorite status
  useEffect(() => {
    api.getFavorites().then(res => setIsFavorite((res.data.project_ids || []).includes(id))).catch(() => {});
    api.getBookmarks().then(res => setIsBookmarked((res.data.project_ids || []).includes(id))).catch(() => {});
  }, [id]);

  // Warn (once) when opening a deal already claimed by someone else
  useEffect(() => {
    if (!project || isAcMember || !currentUser) return;
    if (project.owner_id && project.owner_id !== currentUser.id && warnedRef.current !== project.id) {
      warnedRef.current = project.id;
      setClaimWarn(project.owner_name || 'another team member');
    }
  }, [project, isAcMember, currentUser]);

  // Viewing a claimable deal: a gentle "claim or keep reviewing" nudge at 10s,
  // then a full "claim or pass" decision prompt at 60s (once per deal/session).
  useEffect(() => {
    if (!project || isAcMember || !currentUser) return undefined;
    if (project.pipeline_stage === 'passed' || project.pass_proposed) return undefined;
    if (project.owner_id) return undefined; // only nudge for unclaimed deals (no owner at all)

    const dealId = project.id;
    const getSet = (k) => { try { return new Set(JSON.parse(sessionStorage.getItem(k) || '[]')); } catch { return new Set(); } };
    const mark = (k, set) => { set.add(dealId); try { sessionStorage.setItem(k, JSON.stringify([...set])); } catch { /* ignore */ } };
    const nudged = getSet('olta-claim-nudged');
    const decided = getSet('olta-decide-prompted');

    if (!nudged.has(dealId)) {
      nudgeTimerRef.current = setTimeout(() => {
        mark('olta-claim-nudged', nudged);
        nudgeIdRef.current = toast.custom((t) => (
          <div className="bg-card border border-border rounded-sm p-4 shadow-xl w-[330px]" data-testid="claim-nudge-prompt">
            <p className="text-sm font-medium text-foreground mb-1">Reviewing this deal?</p>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">Claim it to take ownership, or keep reviewing — we'll check back shortly.</p>
            <div className="flex items-center gap-2">
              <Button onClick={() => { toast.dismiss(t); startClaim(); }} data-testid="nudge-claim-btn"
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8">Claim</Button>
              <Button variant="ghost" onClick={() => toast.dismiss(t)} data-testid="nudge-review-btn"
                className="text-muted-foreground hover:text-foreground h-8 px-3 text-xs">Let me review</Button>
            </div>
          </div>
        ), { duration: Infinity, position: 'bottom-right' });
      }, 10000);
    }

    if (!decided.has(dealId)) {
      decideTimerRef.current = setTimeout(() => {
        mark('olta-decide-prompted', decided);
        if (nudgeIdRef.current) { toast.dismiss(nudgeIdRef.current); nudgeIdRef.current = null; }
        decideIdRef.current = toast.custom((t) => (
          <div className="bg-card border border-border rounded-sm p-4 shadow-xl w-[330px]" data-testid="claim-pass-prompt">
            <p className="text-sm font-medium text-foreground mb-1">Ready to claim this deal?</p>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">You've spent some time here. Claim it to take ownership and work on it.</p>
            <div className="flex items-center gap-2">
              <Button onClick={() => { toast.dismiss(t); startClaim(); }} data-testid="prompt-claim-btn"
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8">Claim</Button>
              <Button variant="ghost" onClick={() => toast.dismiss(t)} data-testid="prompt-dismiss-btn"
                className="text-muted-foreground hover:text-foreground h-8 px-2 text-xs">Later</Button>
            </div>
          </div>
        ), { duration: Infinity, position: 'bottom-right' });
      }, 60000);
    }

    return () => {
      clearTimeout(nudgeTimerRef.current);
      clearTimeout(decideTimerRef.current);
      if (nudgeIdRef.current) { toast.dismiss(nudgeIdRef.current); nudgeIdRef.current = null; }
      if (decideIdRef.current) { toast.dismiss(decideIdRef.current); decideIdRef.current = null; }
    };
  }, [project, isAcMember, currentUser, startClaim]);

  // ─── Data Fetching ─────────────────────
  const fetchData = useCallback(async () => {
    try {
      const [projRes, cfgRes] = await Promise.all([api.getProject(id), api.getScorecardConfig()]);
      setProject(projRes.data);
      setConfig(cfgRes.data);
      // Record this deal as viewed by the current user (per-user, idempotent, fire-and-forget)
      api.markViewed(id).catch(() => {});
      // Team list is admin-only; don't block page load if it fails
      try { const teamRes = await api.listTeam(); setTeamMembers(teamRes.data || []); } catch {}
    } catch (err) { 
      toast.error('Failed to load project'); 
    }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Process groups: collapsible, auto-opened by pipeline stage, persisted per deal ───
  const [openGroups, setOpenGroups] = useState(null);
  useEffect(() => {
    if (!project || openGroups) return;
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(`olta-deal-groups-${id}`) || '{}'); } catch { saved = {}; }
    const stage = project.pipeline_stage;
    const auto = ['outreach_wip', 'contacted'].includes(stage) ? 'outreach'
      : ['response_received', 'first_call_meeting', 'nda', 'on_site', 'loi_signed', 'due_diligence', 'signing', 'closing'].includes(stage) ? 'engagement'
        : stage === 'passed' ? null : 'analysis';
    const base = { analysis: false, outreach: false, engagement: false, collab: false, records: false };
    if (auto) base[auto] = true;
    setOpenGroups({ ...base, ...saved });
  }, [project, id, openGroups]);
  const toggleGroup = useCallback((g) => {
    setOpenGroups(prev => {
      const next = { ...(prev || {}), [g]: !(prev || {})[g] };
      try { localStorage.setItem(`olta-deal-groups-${id}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [id]);
  const openGroup = useCallback((g) => {
    setOpenGroups(prev => (prev && prev[g]) ? prev : { ...(prev || {}), [g]: true });
  }, []);
  const GROUP_FOR_ANCHOR = {
    'section-financials': 'analysis', 'section-scorecards': 'analysis', 'group-analysis': 'analysis',
    'section-outreach': 'outreach', 'group-outreach': 'outreach',
    'section-meetings': 'engagement', 'section-refcalls': 'engagement', 'group-engagement': 'engagement',
    'section-discussion': 'collab', 'group-collab': 'collab',
    'section-links': 'records', 'section-documents': 'records', 'group-records': 'records',
  };

  // Deep-link: open the containing group, then scroll to the anchor (e.g. #section-discussion)
  const location = useLocation();
  // Deal opened from the Infra-Fund Screener (Schritt 4): its fund note replaces the thesis note on approval
  useEffect(() => {
    const fn = location.state?.fundNote;
    if (fn) { try { sessionStorage.setItem(`fund-note-${id}`, fn); } catch { /* ignore */ } }
  }, [location.state, id]);
  const fundNote = () => {
    try { return location.state?.fundNote || sessionStorage.getItem(`fund-note-${id}`) || ''; } catch { return location.state?.fundNote || ''; }
  };
  useEffect(() => {
    if (!project || !location.hash) return;
    const target = location.hash.slice(1);
    const grp = GROUP_FOR_ANCHOR[target];
    if (grp) openGroup(grp);
    const t = setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, grp ? 700 : 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, location.hash, openGroup]);

  // Unread @-mentions: red dot on the Collaboration chip + auto-open the group
  const [unreadMentions, setUnreadMentions] = useState(0);
  useEffect(() => {
    api.getUnreadMentions(id).then(r => {
      const n = r.data?.count || 0;
      setUnreadMentions(n);
      if (n > 0) openGroup('collab');
    }).catch(() => {});
  }, [id, openGroup]);
  useEffect(() => {
    if (unreadMentions === 0) return;
    let obs;
    const tryObserve = () => {
      const el = document.getElementById('section-discussion');
      if (!el) return false;
      obs = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) {
          api.markMentionsRead(id).catch(() => {});
          setUnreadMentions(0);
          obs.disconnect();
        }
      }, { threshold: 0.2 });
      obs.observe(el);
      return true;
    };
    if (!tryObserve()) {
      const iv = setInterval(() => { if (tryObserve()) clearInterval(iv); }, 500);
      return () => { clearInterval(iv); if (obs) obs.disconnect(); };
    }
    return () => { if (obs) obs.disconnect(); };
  }, [unreadMentions, id]);

  // Refresh and force UI update
  const refresh = async () => {
    try {
      const [projRes, cfgRes] = await Promise.all([api.getProject(id), api.getScorecardConfig()]);
      const freshData = JSON.parse(JSON.stringify(projRes.data));
      setProject(freshData);
      setConfig(cfgRes.data);
      forceUpdate();
    } catch {
      toast.error('Refresh failed');
    }
  };

  // Stable ref for refresh to avoid circular deps in polling callback
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // For brokered live deals, load the still-open broker proposal so Pass can route through
  // the broker decline flow (sends the Absage mail; the reason becomes the internal pass reason).
  useEffect(() => {
    if (project?.deal_type === 'brokered' && project?.pipeline_stage !== 'passed' && id) {
      api.getProjectBrokerProposal(id)
        .then(r => setBrokerProposal(r.data?.proposal || null))
        .catch(() => setBrokerProposal(null));
    } else {
      setBrokerProposal(null);
    }
  }, [project?.deal_type, project?.pipeline_stage, id]);

  const openPass = () => {
    if (project?.deal_type === 'brokered' && brokerProposal) setDeclineOpen(true);
    else setTurnDownDialog(true);
  };

  // ─── Actions ─────────────────────
  // Poll for screen status when any section is processing
  const pollScreenStatus = useCallback(async () => {
    try {
      const res = await api.getScreenStatus(id);
      const ss = res.data.screen_status || {};
      setScreenStatus(ss);

      // If any section completed, refresh project data
      const anyProcessing = Object.values(ss).some(v => v === 'processing');
      if (!anyProcessing) {
        // All done — stop polling and refresh
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        await refreshRef.current();
      }

      // Report errors
      for (const [section, status] of Object.entries(ss)) {
        if (typeof status === 'string' && status.startsWith('error:')) {
          toast.error(`${section} screening failed: ${status.replace('error: ', '')}`);
          setScreenStatus(prev => ({ ...prev, [section]: 'idle' }));
        }
      }
    } catch {
      // Poll retry — silent failure is expected
    }
  }, [id]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollScreenStatus, 3000);
  }, [pollScreenStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleScreenStarted = () => {
    startPolling();
  };

  const handleStageChange = async (stage) => {
    if (project?.pipeline_stage === 'passed' && stage !== 'passed') {
      setStageNote({ stage, title: 'Deal reaktivieren',
        desc: 'Kurze Begründung (optional) — geht als Info an den ursprünglichen Passer.',
        cta: 'Reaktivieren', note: '' });
      return;
    }
    if (stage === 'olta_approved') {
      const fromFund = isAdmin ? fundNote().trim() : '';
      const aiReason = fromFund || (isAdmin ? (project?.thesis_fit?.reason || '').trim() : '');
      setStageNote({ stage, title: 'Deal freigeben',
        desc: fromFund
          ? 'Note aus dem Infra-Fonds-Screening (Schritt 4) übernommen — erscheint bei den Approvals. (anpassbar)'
          : 'Warum passt der Deal? Diese Note erscheint bei den Approvals. (optional)',
        cta: 'Freigeben', note: aiReason });
      return;
    }
    await applyStageChange(stage);
  };

  const applyStageChange = async (stage, reason, prioCriteria) => {
    try { const r = await api.updateStage(id, stage, (reason || '').trim() || undefined, prioCriteria); toast.success(r.data?.message || 'Stage updated'); await refresh(); }
    catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'stage_gate') setGateInfo(d);
      else toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  const togglePdf = async (docId) => {
    if (showPdf && viewingDocId === docId) { setShowPdf(false); if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); setViewingDocId(null); return; }
    try {
      const res = docId ? await api.getDocumentBlob(id, docId) : await api.getPdfBlob(id);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(URL.createObjectURL(res.data));
      setViewingDocId(docId || null);
      setShowPdf(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load document'); }
  };

  const [uploadProgress, setUploadProgress] = useState('');

  const handleDocUpload = async () => {
    if (!docFile) return;
    setDocUploading(true);
    setUploadProgress('Initializing...');
    try {
      const CHUNK_SIZE = 512 * 1024; // 512KB chunks
      const totalChunks = Math.ceil(docFile.size / CHUNK_SIZE);
      const initRes = await api.initChunkedProject(docFile.name, totalChunks);
      const uploadId = initRes.data.upload_id;

      for (let i = 0; i < totalChunks; i++) {
        setUploadProgress(`Uploading chunk ${i + 1}/${totalChunks}...`);
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, docFile.size);
        const chunk = docFile.slice(start, end);
        // Retry each chunk up to 3 times for transient failures
        let attempts = 0;
        while (attempts < 3) {
          try { await api.uploadChunk(uploadId, i, chunk); break; }
          catch (chunkErr) {
            attempts++;
            if (attempts >= 3) throw chunkErr;
            await new Promise(r => setTimeout(r, 1000 * attempts));
          }
        }
      }

      setUploadProgress('Finalizing...');
      await api.completeChunkedUpload(uploadId, id, docLabel);
      toast.success('Document uploaded');
      setDocUploadOpen(false); setDocFile(null); setDocLabel('CIM');
      setUploadProgress('');
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(`Upload error: ${detail}`);
    }
    finally { setDocUploading(false); setUploadProgress(''); }
  };

  const togglePassTag = (tid) => {
    setTurnDownTags(prev => {
      const next = prev.includes(tid) ? prev.filter(x => x !== tid) : [...prev, tid];
      setTurnDownReason(passReasonText(next));
      return next;
    });
  };

  const handleTurnDown = async () => {
    if (!turnDownReason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      const corrected = turnDownStage && turnDownStage !== project.pipeline_stage ? turnDownStage : undefined;
      const tdRes = await api.turnDownProject(id, turnDownReason, turnDownBench, undefined, turnDownTags, passBrokerSkip, corrected);
      toast.success(passBrokerSkip ? 'Intern gepasst — keine Absage an den Broker gesendet' : (tdRes.data?.message || 'Deal passed'));
      setTurnDownDialog(false);
      setTurnDownReason('');
      setTurnDownTags([]);
      setTurnDownBench(false);
      setTurnDownStage('');
      setPassBrokerSkip(false);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (phase) => {
    const map = {};
    project[phase].scores.forEach(s => { map[s.criteria_id] = { score: s.score, reason: '' }; });
    setEditedScores(map);
    setEditMode(phase);
  };

  const saveOverrides = async () => {
    // Only send criteria where score actually changed
    const original = {};
    project[editMode].scores.forEach(s => { original[s.criteria_id] = s.score; });
    const changed = Object.entries(editedScores).filter(([cid, val]) => val.score !== original[cid]);
    if (changed.length === 0) { toast.error('No scores changed'); return; }
    const missingReason = changed.find(([, val]) => !val.reason.trim());
    if (missingReason) { toast.error('Please provide a reason for each score change'); return; }
    const overrides = changed.map(([cid, val]) => ({ criteria_id: cid, score: val.score, notes: val.reason.trim() }));
    try { await api.overrideScores(id, editMode, overrides); toast.success('Scores updated'); setEditMode(null); setEditedScores({}); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveNote = async (phase, criteriaId) => {
    try { await api.updateNotes(id, phase, criteriaId, { notes: noteText }); toast.success('Note saved'); setEditingNote(null); setNoteText(''); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const toggleBrokerRequested = async (phase, criteriaId, current) => {
    try { await api.updateNotes(id, phase, criteriaId, { broker_requested: !current }); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const searchForHints = async (phase) => {
    setReviewingPhase(phase);
    try { await api.reviewMissing(id, phase); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Search failed'); }
    finally { setReviewingPhase(null); }
  };

  const confirmFound = async () => {
    if (!foundDialog || !foundNote.trim()) { toast.error('Describe where you found the info'); return; }
    try {
      await api.confirmCriteria(id, foundDialog.phase, [{ criteria_id: foundDialog.criteriaId, status: 'found' }]);
      await api.updateNotes(id, foundDialog.phase, foundDialog.criteriaId, { notes: foundNote.trim() });
      toast.success('Marked as found — override the score');
      setFoundDialog(null); setFoundNote('');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const confirmMissing = async (phase, criteriaId) => {
    try {
      await api.confirmCriteria(id, phase, [{ criteria_id: criteriaId, status: 'confirmed_missing' }]);
      toast.success('Will be requested from broker');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDraftEmail = async (phase) => {
    setEmailLoading(true); setEmailDialogOpen(true); setEmailDraft(null); setCopied(false);
    try {
      const res = await api.draftBrokerEmail(id, phase);
      setEmailDraft(res.data);
      // Stage may auto-change — refresh to show it
      await refresh();
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); setEmailDialogOpen(false); }
    finally { setEmailLoading(false); }
  };

  const copyEmail = () => {
    if (emailDraft?.email) { navigator.clipboard.writeText(emailDraft.email); setCopied(true); toast.success('Copied'); setTimeout(() => setCopied(false), 2000); }
  };

  const handleGenerateLetter = async () => {
    setGeneratingLetter(true);
    try {
      await api.generateLetter(id);
      toast.success('Letter generated — view it in the Outreach tab');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate letter'); }
    setGeneratingLetter(false);
  };

  // ─── Guards ─────────────────────
  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  if (!project) return <div className="text-center py-20 text-muted-foreground">Project not found</div>;

  // Backward compatibility for old projects missing new fields
  if (!project.documents) project.documents = [];
  if (!project.photos) project.photos = [];
  if (!project.phase1_history) project.phase1_history = [];
  if (!project.deal_type) project.deal_type = 'brokered';
  if (!project.events) project.events = [];

  const statusBadge = (st) => {
    if (st === 'ko') return <span className="badge-ko rounded-sm">K.O.</span>;
    if (st === 'passed') return <span className="badge-pass rounded-sm">PASSED</span>;
    if (st === 'missing_info') return <span className="badge-pending rounded-sm" style={{ background: 'rgba(217,119,6,0.2)', color: '#f59e0b', borderColor: 'rgba(217,119,6,0.4)' }}>MISSING INFO</span>;
    return <span className="badge-pending rounded-sm">PENDING</span>;
  };

  // ─── Scorecard render uses extracted ScorecardPanel component ─────
  const renderPhase = (phase) => (
    <ScorecardPanel
      phase={phase} project={project} config={config}
      dealType={project.deal_type} isAcMember={isAcMember}
      editMode={editMode} editedScores={editedScores} setEditedScores={setEditedScores}
      expandedCriteria={expandedCriteria} setExpandedCriteria={setExpandedCriteria}
      editingNote={editingNote} setEditingNote={setEditingNote}
      noteText={noteText} setNoteText={setNoteText}
      reviewingPhase={reviewingPhase}
      foundDialog={foundDialog} setFoundDialog={setFoundDialog} setFoundNote={setFoundNote}
      onStartEdit={startEdit} onSaveOverrides={saveOverrides} onCancelEdit={() => setEditMode(null)}
      onSaveNote={saveNote} onToggleBrokerRequested={toggleBrokerRequested}
      onSearchForHints={searchForHints} onConfirmMissing={confirmMissing} onDraftEmail={handleDraftEmail}
      projectId={id} onRefresh={refresh}
    />
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="project-detail-page">
      {/* Row 1: Back + Name + Badges — sticky on mobile so the back button stays reachable */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 max-lg:sticky max-lg:-top-14 max-lg:z-30 max-lg:-mx-4 max-lg:px-4 max-lg:pl-12 max-lg:py-2 max-lg:bg-background/95 max-lg:backdrop-blur-sm max-lg:border-b max-lg:border-border/40">
          <Button variant="ghost" data-testid="back-to-dashboard" onClick={() => requestLeave(() => { if (window.history.length > 1) navigate(-1); else navigate('/'); })} className="text-muted-foreground hover:text-foreground rounded-sm h-8 w-8 p-0 shrink-0"><ArrowLeft className="w-4 h-4" /></Button>
          <button onClick={async () => {
            try { const res = await api.toggleFavorite(id); setIsFavorite(res.data.action === 'added'); }
            catch {} }}
            className={`shrink-0 transition-colors ${isFavorite ? 'text-amber-400' : 'text-muted-foreground/20 hover:text-amber-400/50'}`}
            data-testid="deal-favorite-btn">
            <Star className="w-5 h-5" fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
          <button onClick={async () => {
            try { const res = await api.toggleBookmark(id); setIsBookmarked(res.data.action === 'added'); }
            catch {} }}
            className={`shrink-0 transition-colors ${isBookmarked ? 'text-blue-400' : 'text-muted-foreground/20 hover:text-blue-400/50'}`}
            data-testid="deal-bookmark-btn" title={isBookmarked ? 'Remove bookmark' : 'Bookmark (private)'}>
            <Bookmark className="w-5 h-5" fill={isBookmarked ? 'currentColor' : 'none'} />
          </button>
          <h1 className="font-heading text-xl text-foreground tracking-tight truncate">{project.name}</h1>
          {!isAcMember && (
            <button onClick={async () => {
              const v = window.prompt('Deal-Titel / Firmennamen korrigieren:', project?.name || '');
              if (v === null) return;
              const name = v.trim();
              if (!name || name === project.name) return;
              try { await api.updateCompanyInfo(id, { name }); toast.success('Name aktualisiert'); await refresh(); }
              catch (err) { toast.error(err.response?.data?.detail || 'Umbenennen fehlgeschlagen'); }
            }} data-testid="rename-project-btn" title="Firmennamen / Deal-Titel korrigieren"
              className="text-muted-foreground/40 hover:text-foreground transition-colors shrink-0">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {project.random_project_name && (
            <span className="text-xs font-mono text-blue-400/90 bg-blue-500/10 border border-blue-500/20 rounded px-1.5 py-0.5 shrink-0 flex items-center gap-1" data-testid="deal-code-name" title={project.name_definition ? `${project.name_topic}\n${project.name_definition}` : "Confidential project code name"}>
              <Fish className="w-3 h-3" /> {project.random_project_name}
            </span>
          )}
          {statusBadge(project.status)}
          <PrioBadge prio={project.prio} testid="project-prio-badge" description={prioCfg?.descriptions?.[project.prio]} />
          {!isAcMember && project.pipeline_stage !== 'passed' && <PrioEditButton project={project} onSaved={refresh} />}
          {!isAcMember && <ImportCategoryChip project={project} onSaved={refresh} />}
          {project.deal_type === 'proprietary' && <span className="text-[9px] text-purple-400 border border-purple-600/30 bg-purple-600/10 rounded px-1.5 py-0.5 font-mono uppercase shrink-0">Proprietary</span>}
          {project.letter_returned && (
            <span className="inline-flex items-center gap-1.5 text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 uppercase shrink-0 border-orange-500/50 bg-orange-500/15 text-orange-300" data-testid="letter-returned-badge"
              title={`Brief unzustellbar zurück am ${new Date(project.letter_returned.at).toLocaleDateString('de-DE')} (${project.letter_returned.by_name})${project.letter_returned.note ? ` — ${project.letter_returned.note}` : ''}. Sequenz abgebrochen, keine Tasks bis zur Reaktivierung.`}>
              <MailWarning className="w-3 h-3" /> Brief unzustellbar — pausiert
              {!isAcMember && (
                <button onClick={async () => {
                  if (!window.confirm('Deal reaktivieren? Er geht zurück nach Outreach WIP — Adresse am Kontakt korrigieren, Brief neu finalisieren, drucken und versenden.')) return;
                  try { const r = await api.letterReturnedReactivate(id); toast.success(r.data.message); await refresh(); }
                  catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                }} data-testid="letter-returned-reactivate-btn" className="ml-1 inline-flex items-center gap-1 normal-case font-sans font-medium text-orange-200 hover:text-white underline-offset-2 hover:underline">
                  <RotateCcw className="w-3 h-3" /> Reaktivieren → Outreach WIP
                </button>
              )}
            </span>
          )}
        </div>

      {/* Row 2: Meta info + Owner + Stage + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 pl-1 sm:pl-11 !mt-3">
          {/* Left: meta info */}
          <div className="flex items-center gap-2 sm:gap-3 text-[10px] text-muted-foreground flex-wrap">
            <span>{new Date(project.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} by {project.created_by_name}</span>
            {project.company_website && (
              <a href={project.company_website.startsWith('http') ? project.company_website : `https://${project.company_website}`}
                target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                <Globe className="w-2.5 h-2.5" />{project.company_website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            )}
            {project.first_contact_date && <span className="text-emerald-400 font-mono">Contact: {project.first_contact_date}</span>}
          </div>

          {/* Right: Owner + Stage + Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Owner */}
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] text-muted-foreground/50 uppercase">Owner:</span>
              {isAdmin ? (
                <Select value={project.owner_id || ''} onValueChange={async (ownerId) => {
                  try { await api.claimDeal(id, ownerId); toast.success('Updated'); await refresh(); } catch {} }}>
                  <SelectTrigger className="w-28 bg-secondary border-border text-foreground rounded-sm h-6 text-[10px]" data-testid="owner-selector"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-sm">
                    {teamMembers.filter(m => m.role !== 'ac_member').map(m => (<SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-[11px] text-foreground">{project.owner_name || 'Unassigned'}</span>
              )}
              {!isAcMember && currentUser && project.owner_id !== currentUser.id && (
                <button onClick={startClaim}
                  className="text-[9px] text-blue-400 hover:text-blue-300" data-testid="claim-deal-btn">Claim</button>
              )}
            </div>

            <span className="text-border">|</span>

            {/* Stage */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-foreground" data-testid="current-stage">
                {PIPELINE_STAGES.find(s => s.value === project.pipeline_stage)?.label || project.pipeline_stage}
              </span>
              {isAcMember ? (project.ac_flagged && (
                <span data-testid="ac-flagged-badge" title={`Fürs AC-Meeting vorgemerkt${project.ac_flagged_by ? ` von ${project.ac_flagged_by}` : ''}`}
                  className="text-[9px] font-mono font-bold text-purple-300 bg-purple-500/10 border border-purple-500/40 rounded px-1.5 py-0.5">
                  ! AC
                </span>
              )) : project.pipeline_stage !== 'passed' && (
                <button data-testid="ac-flag-toggle-btn"
                  onClick={async () => {
                    const next = !project.ac_flagged;
                    try {
                      await api.setAcFlag(id, next);
                      toast.success(next ? 'Fürs AC-Meeting vorgemerkt (!)' : 'AC-Vormerkung entfernt');
                      await refresh();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
                  }}
                  title={project.ac_flagged
                    ? `Fürs AC-Meeting vorgemerkt${project.ac_flagged_by ? ` von ${project.ac_flagged_by}` : ''} — Klick entfernt die Vormerkung`
                    : 'Fürs nächste AC-Meeting vormerken (!) — der Deal kommt in die AC-Agenda-E-Mail'}
                  className={`text-[9px] font-mono font-bold rounded px-1.5 py-0.5 border transition-colors ${project.ac_flagged
                    ? 'text-purple-300 bg-purple-500/10 border-purple-500/40 hover:bg-purple-500/20'
                    : 'text-muted-foreground/50 border-dashed border-border hover:text-purple-300 hover:border-purple-500/40'}`}>
                  ! AC
                </button>
              )}
              {!isAcMember && (() => {
                const currentIdx = PIPELINE_STAGES.findIndex(s => s.value === project.pipeline_stage);
                const nextStage = currentIdx >= 0 && currentIdx < PIPELINE_STAGES.length - 1 ? PIPELINE_STAGES[currentIdx + 1] : null;
                if (!nextStage || nextStage.value === 'passed' || project.pipeline_stage === 'passed') return null;
                // Approval already proposed → no second "send for approval" (Max, 20.10.2026); the banner below handles it
                if (nextStage.value === 'olta_approved' && project.approval_proposed) return null;
                return (
                  <Button onClick={() => handleStageChange(nextStage.value)} size="sm" data-testid="advance-stage-btn"
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-[9px] h-6 px-2">
                    → {nextStage.label}
                  </Button>
                );
              })()}
              {!isAcMember && project.deal_type === 'brokered' && (
                <Select value={project.brokered_stage_override || 'auto'} onValueChange={async (v) => {
                  try {
                    await api.setBrokeredStage(id, v === 'auto' ? '' : v);
                    toast.success(v === 'auto' ? 'Broker-Prozess: wieder automatisch abgeleitet' : `Broker-Prozess-Stage gesetzt: ${BROKERED_STAGES.find(s => s.value === v)?.label || v}`);
                    await refresh();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
                }}>
                  <SelectTrigger className="w-44 bg-secondary border-amber-500/30 text-foreground rounded-sm h-6 text-[10px]"
                    data-testid="brokered-stage-selector"
                    title="Broker-Prozess-Stage (Brokered Funnel) — manuell setzen oder automatisch ableiten lassen">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-sm">
                    <SelectItem value="auto" className="text-xs" data-testid="brokered-stage-option-auto">
                      Auto{project.brokered_funnel_stage_auto_label ? `: ${project.brokered_funnel_stage_auto_label}` : ''}
                    </SelectItem>
                    {BROKERED_STAGES.map(s => (
                      <SelectItem key={s.value} value={s.value} className="text-xs" data-testid={`brokered-stage-option-${s.value}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!isAcMember && project.deal_type === 'brokered' && (
                <label className="inline-flex items-center gap-1 text-[9px] font-mono text-muted-foreground" title="Exposé-Eingang (E-Mail-Zeitstempel) — Basis für Talus Exposé-SLA; Standard = Anlegezeitpunkt">
                  Eingang
                  <input type="datetime-local" data-testid="broker-received-at-input"
                    value={toLocalInput(project.received_at || project.created_at)}
                    onChange={async (e) => {
                      if (!e.target.value) return;
                      try {
                        await api.setBrokerReceivedAt(id, new Date(e.target.value).toISOString());
                        toast.success('Exposé-Eingang gespeichert');
                        await refresh();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Eingang konnte nicht gespeichert werden'); }
                    }}
                    className="h-6 bg-secondary border border-border rounded-sm px-1 text-[10px] text-foreground" />
                </label>
              )}
            </div>

            <span className="text-border">|</span>

            {/* Pass + More */}
            {!isAcMember && (
              <div className="flex items-center gap-1">
                {project.pipeline_stage !== 'passed' && !project.pass_proposed && (
                  <Button onClick={openPass} size="sm" data-testid="pass-deal-btn"
                    className="bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-900/30 rounded-sm text-[9px] h-6 px-2">
                    Pass
                  </Button>
                )}
                <div className="relative">
                  <Button onClick={() => setMoreOpen(!moreOpen)} size="sm" variant="ghost" data-testid="deal-more-menu-btn"
                    className="text-muted-foreground hover:text-foreground h-6 w-6 p-0 rounded-sm">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                  {moreOpen && (
                    <div className="absolute right-0 top-7 bg-card border border-border rounded-sm shadow-lg z-50 py-1 w-48" onMouseLeave={() => setMoreOpen(false)}>
                      <button onClick={() => { setDocUploadOpen(true); setMoreOpen(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                        <Upload className="w-3.5 h-3.5" /> Upload Document
                      </button>
                      {isAdmin && (
                      <a href={`${API_BASE}/api/projects/${id}/export/excel`} target="_blank" rel="noopener noreferrer"
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                        <Download className="w-3.5 h-3.5" /> Export Excel
                      </a>
                      )}
                      {isAdmin && (
                      <a href={`${API_BASE}/api/projects/${id}/export/pdf`} target="_blank" rel="noopener noreferrer"
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                        <Download className="w-3.5 h-3.5" /> Export PDF
                      </a>
                      )}
                      <button onClick={async () => {
                        try { await api.addToAgenda(id); toast.success('Added to meeting'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                        setMoreOpen(false);
                      }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                        <CalendarPlus className="w-3.5 h-3.5" /> Add to Meeting
                      </button>
                      <button onClick={() => { setHintText(project.meeting_hint || ''); setHintDialog(true); setMoreOpen(false); }}
                        data-testid="meeting-hint-menu-btn"
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                        <Lightbulb className={`w-3.5 h-3.5 ${project.meeting_hint ? 'text-amber-400' : ''}`} /> Meeting-Notiz{project.meeting_hint ? ' ✓' : ''}
                      </button>
                      {!isAcMember && !project.letter_returned && ['contacted', 'response_received'].includes(project.pipeline_stage) && (
                        <button onClick={() => { setReturnedNote(''); setReturnedDialog(true); setMoreOpen(false); }} data-testid="letter-returned-menu-btn"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                          <MailWarning className="w-3.5 h-3.5 text-orange-400" /> Brief unzustellbar zurück…
                        </button>
                      )}
                      {isAdmin && project.pipeline_stage !== 'passed' && PIPELINE_STAGES.findIndex(s => s.value === project.pipeline_stage) > 0 && (
                        <button onClick={() => {
                          const idx = PIPELINE_STAGES.findIndex(s => s.value === project.pipeline_stage);
                          setStageBackTarget(PIPELINE_STAGES[idx - 1]?.value || '');
                          setStageBackOpen(true); setMoreOpen(false);
                        }} data-testid="stage-back-menu-btn"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                          <Undo2 className="w-3.5 h-3.5" /> Stage zurücksetzen…
                        </button>
                      )}
                      {(isAdmin || ['olta_approved', 'outreach_wip', 'contacted', 'first_call_meeting', 'nda', 'on_site', 'loi_signed', 'due_diligence', 'signing', 'closing'].includes(project.pipeline_stage)) &&
                       currentUser && ['talu', 'maximilian', 'max'].some(n => (currentUser.name || '').toLowerCase().includes(n)) && (
                        <button onClick={() => { handleGenerateLetter(); setMoreOpen(false); }} disabled={generatingLetter}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-secondary transition-colors">
                          <PenTool className="w-3.5 h-3.5" /> {generatingLetter ? 'Generating...' : 'Generate Letter'}
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={async () => {
                          if (!window.confirm(`Delete "${project.name}"?\n\nThis permanently removes the deal and all its data (comments, activity, documents, meeting agenda). This cannot be undone.`)) return;
                          try { await api.deleteProject(id); toast.success('Deal deleted'); navigate('/pipeline'); }
                          catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          setMoreOpen(false);
                        }} data-testid="delete-deal-btn" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-900/10 transition-colors border-t border-border/50 mt-1">
                          <XCircle className="w-3.5 h-3.5" /> Delete Deal
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      {/* Sticky section jump navigation */}
      <SectionNav items={[
        ...(!isAcMember ? [{ id: 'section-workflow', label: 'Workflow' }] : []),
        { id: 'section-summary', label: 'Summary' },
        { id: 'group-analysis', label: 'Analysis', group: 'analysis' },
        ...(project.deal_type === 'brokered'
          ? [{ id: 'group-engagement', label: 'Engagement', group: 'engagement' },
             ...(!isAcMember ? [{ id: 'group-outreach', label: 'Outreach', group: 'outreach' }] : [])]
          : [...(!isAcMember ? [{ id: 'group-outreach', label: 'Outreach', group: 'outreach' }] : []),
             { id: 'group-engagement', label: 'Engagement', group: 'engagement' }]),
        { id: 'group-collab', label: 'Collaboration', group: 'collab', dot: unreadMentions > 0 },
        { id: 'group-records', label: 'Records', group: 'records' },
      ]} onJump={(it) => { if (it.group) openGroup(it.group); }} />

      {/* Ask OLTA AI — deal-scoped Q&A over everything logged on this page */}
      <AskOltaAI projectId={id} dealName={project.name} />

      {/* Pass Proposed Banner */}
      {project.pass_proposed && project.pipeline_stage !== 'passed' && (
        <PassProposedBanner project={project} id={id} isAcMember={isAcMember} currentUser={currentUser} refresh={refresh} />
      )}

      {/* Passed Banner — reason + bench status */}
      {project.pipeline_stage === 'passed' && (
        <PassedBanner project={project} id={id} refresh={refresh}
          isAdmin={isAdmin || (project.turned_down?.by_id === currentUser?.id && project.turned_down?.at
            && (Date.now() - new Date(project.turned_down.at).getTime()) <= 7 * 86400000)} />
      )}

      {/* AI detected a negative owner reply → stop sequence & pass? */}
      {project.negative_response_prompt && project.pipeline_stage !== 'passed' && !isAcMember && (
        <NegativeResponseBanner project={project} id={id} refresh={refresh} />
      )}

      {/* Landing/email response → one-click Response Received */}
      {project.response_suggestion && project.pipeline_stage === 'contacted' && !isAcMember && (() => {
        const sug = project.response_suggestion;
        const isAi = sug.source === 'email_ai';
        const isEmail = sug.source === 'email' || isAi;
        const tone = isAi
          ? { box: 'bg-amber-950/20 border-amber-800/30', text: 'text-amber-400', btn: 'bg-amber-700 hover:bg-amber-600', outline: 'border-amber-700/40 text-amber-400 hover:bg-amber-600/10' }
          : { box: 'bg-emerald-950/20 border-emerald-800/30', text: 'text-emerald-400', btn: 'bg-emerald-700 hover:bg-emerald-600', outline: 'border-emerald-700/40 text-emerald-400 hover:bg-emerald-600/10' };
        return (
        <div className={`border rounded-sm p-4 ${tone.box}`} data-testid="response-suggestion-banner">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {isEmail ? <Mail className={`w-4 h-4 ${tone.text}`} /> : <PhoneIncoming className={`w-4 h-4 ${tone.text}`} />}
            <p className={`text-sm font-medium ${tone.text}`}>
              {isAi ? 'Possible match: inbound email (AI)' : isEmail ? 'Inbound email from the company' : 'Response via landing page'}
            </p>
            <span className="text-[10px] text-muted-foreground font-mono">
              {sug.label}{sug.name ? ` — ${sug.name}` : ''}
              {sug.email ? ` <${sug.email}>` : ''} · {new Date(sug.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <p className="text-xs text-muted-foreground ml-6 mb-3">
            {isAi
              ? <>This email has a different domain, but its content suggests it belongs to this deal{sug.subject ? <> (&ldquo;{sug.subject}&rdquo;)</> : null}.
                  {sug.reason ? <> <span className="text-amber-400/90">AI reasoning: {sug.reason}</span></> : null}
                  {sug.confidence ? <span className="font-mono"> · confidence {Math.round(sug.confidence * 100)}%</span> : null} — please verify before acting.</>
              : isEmail
                ? <>An email from the company{sug.subject ? <> (&ldquo;{sug.subject}&rdquo;)</> : null} was detected. Move this deal to &ldquo;Response Received&rdquo;?</>
                : <>The entrepreneur responded via the landing page. Move this deal to &ldquo;Response Received&rdquo;?</>}
          </p>
          <div className="ml-6 flex items-center gap-2 flex-wrap">
            <Button onClick={async () => {
              try {
                await api.updateStage(id, 'response_received');
                await api.dismissResponseSuggestion(id);
                toast.success('Deal moved to Response Received');
                await refresh();
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }} data-testid="response-suggestion-apply-btn"
              className={`${tone.btn} text-white rounded-sm text-xs h-8 px-3`}>
              <Check className="w-3.5 h-3.5 mr-1" /> Set to Response Received
            </Button>
            {isEmail && sug.email && (
              <Button onClick={async () => {
                try {
                  await api.addDealContact(id, {
                    name: sug.name || sug.email,
                    role: 'owner', emails: [sug.email],
                  });
                  toast.success('Sender added as Owner contact — emails will sync from now on');
                  await refresh();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }} data-testid="response-suggestion-add-contact-btn"
                variant="outline" className={`${tone.outline} rounded-sm text-xs h-8 px-3`}>
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Add sender as contact
              </Button>
            )}
            <Button onClick={async () => {
              try { await api.dismissResponseSuggestion(id); await refresh(); }
              catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }} data-testid="response-suggestion-dismiss-btn"
              variant="outline" className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
              {isAi ? 'Wrong match — dismiss' : 'Dismiss'}
            </Button>
          </div>
        </div>
        );
      })()}

      {/* Stage-gate met → one-click move to the next stage (never on passed deals) */}
      {project.stage_suggestion && !isAcMember && project.pipeline_stage !== 'passed'
        && !(project.response_suggestion && project.pipeline_stage === 'contacted' && project.stage_suggestion.target === 'response_received') && (
        <StageSuggestionBanner suggestion={project.stage_suggestion} applying={applyingSuggestion}
          onApply={async () => {
            setApplyingSuggestion(true);
            try {
              await api.updateStage(id, project.stage_suggestion.target);
              toast.success(`Deal moved to ${project.stage_suggestion.target_label}`);
              await refresh();
            } catch (err) {
              const d = err.response?.data?.detail;
              if (d && typeof d === 'object' && d.code === 'stage_gate') setGateInfo(d);
              else toast.error(typeof d === 'string' ? d : 'Failed');
            }
            setApplyingSuggestion(false);
          }}
          onDismiss={async () => {
            try { await api.dismissStageSuggestion(id); await refresh(); }
            catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
          }} />
      )}

      {/* Approval Proposed Banner (OLTA Reviewed → OLTA Approved) */}
      {project.approval_proposed && project.pipeline_stage !== 'olta_approved' && (
        <div className="bg-blue-950/20 border border-blue-800/30 rounded-sm p-4" data-testid="approval-proposed-banner">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-blue-400" />
            <p className="text-sm font-medium text-blue-400">OLTA Approval Proposed</p>
            <span className="text-[10px] text-muted-foreground font-mono">
              by {project.approval_proposed.proposed_by} — {new Date(project.approval_proposed.proposed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
          <p className="text-xs text-muted-foreground ml-6 mb-3" data-testid="approval-pending-from">
            {project.approval_self_final
              ? `${(project.approval_proposed.proposed_by || '').split(' ')[0]} ist zugleich der finale Approver${project.prio ? ` (Prio ${project.prio})` : ''} — keine weitere Bestätigung nötig.`
              : (project.approval_pending_from || []).length
                ? `Wartet auf die finale Freigabe durch ${project.approval_pending_from.map(u => u.name.split(' ')[0]).join(' und ')} → OLTA Approved.`
                : 'Wartet auf die finale Freigabe → OLTA Approved.'}
          </p>
          {project.approval_proposed.note && (
            <p className="text-xs text-sky-300/90 ml-6 mb-2" data-testid="approval-proposed-note">
              <span className="font-medium">{(project.approval_proposed.proposed_by || '').split(' ')[0]}'s Note:</span> "{project.approval_proposed.note}"
            </p>
          )}
          {(isAdmin || project.approval_proposed.proposed_by_id === currentUser?.id) && (
            <button data-testid="edit-approval-note-btn"
              onClick={() => setStageNote({
                mode: 'note-edit', title: 'Note bearbeiten',
                desc: 'Diese Note sehen die Approver bei der Freigabe.',
                cta: 'Speichern', note: project.approval_proposed.note || '',
              })}
              className="ml-6 mb-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-sky-300 transition-colors">
              <Pencil className="w-3 h-3" /> {project.approval_proposed.note ? 'Note bearbeiten' : 'Note hinzufügen'}
            </button>
          )}
          {(project.approval_proposed.confirmations || []).length > 0 && (
            <p className="text-[10px] text-amber-400 ml-6 mb-2">
              Confirmed by: {project.approval_proposed.confirmations.map(c => c.name).join(', ')} — still needs more approvals
            </p>
          )}
          <div className="ml-6 flex items-center gap-2">
            {!isAcMember && currentUser && project.approval_proposed.proposed_by_id !== currentUser?.id
              && !!(project.review_permissions?.approval || {}).can_confirm && (
              <>
                <Button onClick={() => setFinalApprovalOpen(true)} data-testid="confirm-approval-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
                  <Check className="w-3.5 h-3.5 mr-1" /> Prüfen & freigeben
                </Button>
                <Button onClick={async () => {
                  try { await api.overrideApproval(id); toast.success('Approval cancelled'); await refresh(); }
                  catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                }} variant="outline" className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
                  Cancel
                </Button>
              </>
            )}
            {currentUser && project.approval_proposed.proposed_by_id === currentUser?.id && (
              project.approval_self_final ? (
                <Button onClick={() => setFinalApprovalOpen(true)} data-testid="self-final-approval-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
                  <Check className="w-3.5 h-3.5 mr-1" /> Freigabe abschließen
                </Button>
              ) : (
                <span className="text-[10px] text-muted-foreground italic">Awaiting confirmation</span>
              )
            )}
            {currentUser && (project.approval_proposed.confirmations || []).some(c => c.id === currentUser?.id) && (
              <span className="text-[10px] text-amber-400 italic">You confirmed — waiting for additional approval</span>
            )}
          </div>
        </div>
      )}

      {finalApprovalOpen && <FinalApprovalDialog project={project} onClose={() => setFinalApprovalOpen(false)} onDone={refresh} />}

      {/* Contact Proposed Banner */}
      {project.contact_proposed && project.pipeline_stage !== 'contacted' && (
        <div className="bg-blue-950/20 border border-blue-800/30 rounded-sm p-4" data-testid="contact-proposed-banner">
          <div className="flex items-center gap-2 mb-2">
            <ExternalLink className="w-4 h-4 text-blue-400" />
            <p className="text-sm font-medium text-blue-400">Contact Proposed</p>
            <span className="text-[10px] text-muted-foreground font-mono">
              by {project.contact_proposed.proposed_by} — {new Date(project.contact_proposed.proposed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
          <p className="text-xs text-muted-foreground ml-6 mb-3" data-testid="contact-pending-from">
            {(project.review_permissions?.contact?.pending_from || []).length
              ? `Wartet auf die Bestätigung durch ${project.review_permissions.contact.pending_from.map(u => u.name.split(' ')[0]).join(' und ')} → Contacted.`
              : 'Awaiting approval from the deal approvers to move this deal to Contacted stage.'}
          </p>
          {(project.contact_proposed.confirmations || []).length > 0 && (
            <p className="text-[10px] text-amber-400 ml-6 mb-2">
              Confirmed by: {project.contact_proposed.confirmations.map(c => c.name).join(', ')} — still needs more approvals
            </p>
          )}
          <div className="ml-6 flex items-center gap-2">
            {!isAcMember && currentUser && project.contact_proposed.proposed_by_id !== currentUser?.id
              && !!(project.review_permissions?.contact || {}).can_confirm && (
              <>
                <Button onClick={async () => {
                  try { const res = await api.confirmContact(id); toast.success(res.data.message); await refresh(); }
                  catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                }} data-testid="confirm-contact-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve Contact
                </Button>
                <Button onClick={async () => {
                  try { await api.overrideContact(id); toast.success('Contact proposal cancelled'); await refresh(); }
                  catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                }} data-testid="override-contact-btn"
                  variant="outline" className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
                  Cancel
                </Button>
              </>
            )}
            {currentUser && project.contact_proposed.proposed_by_id === currentUser?.id && (
              <span className="text-[10px] text-muted-foreground italic">Awaiting confirmation from another team member</span>
            )}
            {currentUser && (project.contact_proposed.confirmations || []).some(c => c.id === currentUser?.id) && (
              <span className="text-[10px] text-amber-400 italic">You confirmed — waiting for additional approval</span>
            )}
          </div>
        </div>
      )}

      {/* Stage-gate: missing prerequisites popup */}
      <StageGateDialog gate={gateInfo} onClose={() => setGateInfo(null)} />
      <Dialog open={!!stageNote} onOpenChange={(o) => { if (!o) setStageNote(null); }}>
        <DialogContent className="sm:max-w-xl" data-testid="stage-note-dialog">
          <DialogHeader>
            <DialogTitle>{stageNote?.title}</DialogTitle>
            <DialogDescription>{stageNote?.desc}</DialogDescription>
          </DialogHeader>
          {stageNote?.stage === 'olta_approved' && stageNote?.mode !== 'note-edit' && (
            <PrioCriteriaPicker value={stageNote?.prioCriteria || project.prio_criteria || []}
              onChange={(ids) => setStageNote((s) => ({ ...s, prioCriteria: ids }))} />
          )}
          <Textarea value={stageNote?.note || ''} rows={7} autoFocus
            onChange={(e) => setStageNote((s) => ({ ...s, note: e.target.value }))}
            placeholder="Begründung…" data-testid="stage-note-input"
            className="text-sm leading-relaxed min-h-[160px]" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStageNote(null)} data-testid="stage-note-cancel-btn">Abbrechen</Button>
            {(() => {
              const prioPick = stageNote?.stage === 'olta_approved' && stageNote?.mode !== 'note-edit';
              const picked = prioPick ? computePrioWith(stageNote?.prioCriteria || project.prio_criteria || [], prioCfg?.rules, prioCfg?.criteria) : null;
              const isPass = picked === 'PASS';
              return (
                <Button onClick={async () => {
                  const s = stageNote; setStageNote(null);
                  if (s.mode === 'note-edit') {
                    try { await api.updateApprovalNote(id, { note: s.note }); toast.success('Note aktualisiert'); await refresh(); }
                    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  } else if (isPass) {
                    // criteria below the B rule (or no succession case on B level) → pass + bench instead of an approval proposal
                    try {
                      const reason = (s.note || '').trim() || 'Prio-Kriterien nicht erfüllt (unter Prio B)';
                      const res = await api.turnDownProject(id, reason, true, 'prio', ['prio_criteria']);
                      toast.success(res.data.message || 'Pass (Bench) vorgeschlagen'); await refresh();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  } else {
                    await applyStageChange(s.stage, s.note, prioPick ? (s.prioCriteria || project.prio_criteria || []) : undefined);
                  }
                }}
                  className={isPass ? 'bg-zinc-600 hover:bg-zinc-500 text-white' : ''}
                  data-testid="stage-note-confirm-btn">
                  {isPass ? 'Als Pass (Bench) vorschlagen' : `${stageNote?.cta}${prioPick ? ` als ${PRIO_LABEL(picked)}` : ''}`}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={docUploadOpen} onOpenChange={setDocUploadOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2"><Upload className="w-5 h-5 text-blue-400" /> Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Document Type</Label>
              <Select value={docLabel} onValueChange={setDocLabel}>
                <SelectTrigger className="bg-secondary border-border rounded-sm h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border rounded-sm">
                  <SelectItem value="Teaser" className="text-xs">Teaser</SelectItem>
                  <SelectItem value="CIM" className="text-xs">CIM</SelectItem>
                  <SelectItem value="NDA" className="text-xs">NDA</SelectItem>
                  <SelectItem value="LOI (Signed)" className="text-xs">LOI (Signed)</SelectItem>
                  <SelectItem value="Financial Model" className="text-xs">Financial Model</SelectItem>
                  <SelectItem value="Other" className="text-xs">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border border-dashed border-border rounded-sm p-4 text-center">
              <input type="file" onChange={e => setDocFile(e.target.files[0])} className="hidden" id="proj-doc-upload" />
              <label htmlFor="proj-doc-upload" className="cursor-pointer"><Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1" /><p className="text-xs text-muted-foreground">{docFile ? docFile.name : 'Select file'}</p></label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDocUploadOpen(false)} className="text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleDocUpload} disabled={!docFile || docUploading} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
                {docUploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />{uploadProgress || 'Uploading...'}</> : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Found Dialog */}
      <Dialog open={!!foundDialog} onOpenChange={o => { if (!o) setFoundDialog(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2"><CheckSquare className="w-5 h-5 text-green-400" /> Found Information</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">Where did you find info for <span className="text-foreground font-medium">"{foundDialog?.criteriaName}"</span>?</p>
            <p className="text-xs text-muted-foreground">Visible to all team members.</p>
            <Input autoFocus value={foundNote} onChange={e => setFoundNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && foundNote.trim()) confirmFound(); }}
              placeholder="e.g. Page 7 — revenue breakdown mentions top 5 clients..." className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" data-testid="found-note-input" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setFoundDialog(null)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={confirmFound} disabled={!foundNote.trim()} className="bg-green-700 hover:bg-green-600 text-white rounded-sm font-medium text-xs h-8 px-3"><Check className="w-3.5 h-3.5 mr-1" /> Confirm Found</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2"><Mail className="w-5 h-5 text-blue-400" /> Broker Email</DialogTitle></DialogHeader>
          {emailLoading && (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto mb-3" /><p className="text-sm text-muted-foreground">Drafting in German...</p></div>
          )}
          {!emailLoading && emailDraft?.email && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground"><span className="font-mono text-blue-400">{emailDraft.missing_criteria_count}</span> items requested</p>
                <Button onClick={copyEmail} className={`rounded-sm font-medium text-xs h-8 px-3 ${copied ? 'bg-green-700 text-green-100' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                  {copied ? <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Copied!</span> : <span className="flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy</span>}
                </Button>
              </div>
              <ScrollArea className="max-h-[50vh]"><div className="bg-secondary/50 border border-border rounded-sm p-4"><pre className="text-sm text-foreground whitespace-pre-wrap font-body leading-relaxed">{emailDraft.email}</pre></div></ScrollArea>
            </div>
          )}
          {!emailLoading && !emailDraft?.email && emailDraft?.message && (
            <div className="py-8 text-center"><CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" /><p className="text-sm">{emailDraft.message}</p></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Already-claimed warning */}
      <Dialog open={!!claimWarn} onOpenChange={o => { if (!o) setClaimWarn(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="claimed-warning-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2"><AlertCircle className="w-5 h-5 text-amber-400" /> Deal Already Claimed</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This deal is already claimed by <span className="text-foreground font-medium">{claimWarn}</span>. Please coordinate before working on it to avoid duplicate effort.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setClaimWarn(null)} data-testid="claimed-warning-dismiss"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">Got it</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Code Name "Namer" — shown when claiming an unnamed deal */}
      <Dialog open={namerOpen} onOpenChange={o => { if (!o && !namerClaiming) setNamerOpen(false); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="namer-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2"><Fish className="w-5 h-5 text-blue-400" /> Name this Deal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Claiming this deal assigns a confidential code name. Hit shuffle until you find one you like.
            </p>
            <div className="py-4 px-3 rounded-sm bg-secondary border border-border text-center min-h-[120px] flex flex-col items-center justify-center gap-1.5" data-testid="namer-display">
              {namerShuffling ? (
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
              ) : (
                <>
                  <span className="text-xl font-heading text-foreground tracking-tight" data-testid="namer-candidate">{namerCandidate || '—'}</span>
                  {namerMeta.topic && (
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-400/90" data-testid="namer-topic">{namerMeta.topic}</span>
                  )}
                  {namerMeta.definition && (
                    <p className="text-xs italic text-muted-foreground leading-snug max-w-[280px]" data-testid="namer-definition">{namerMeta.definition}</p>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <Button onClick={() => shuffleName(false)} disabled={namerShuffling || namerClaiming} variant="ghost" data-testid="namer-shuffle-btn"
                className="text-xs h-9 px-3 border border-border rounded-sm text-foreground hover:bg-secondary">
                <Shuffle className="w-3.5 h-3.5 mr-1.5" /> Shuffle
              </Button>
              <Button onClick={confirmClaim} disabled={!namerCandidate || namerShuffling || namerClaiming} data-testid="namer-confirm-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-9 px-4 flex-1">
                {namerClaiming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Claim with this name'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Turn Down Dialog */}
      <Dialog open={turnDownDialog} onOpenChange={(o) => { setTurnDownDialog(o); if (!o) { setTurnDownTags([]); setTurnDownReason(''); setPassBrokerSkip(false); setTurnDownStage(''); } }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2"><XCircle className="w-5 h-5 text-red-400" /> Pass on Deal</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {passBrokerSkip && (
              <div className="flex items-start gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-2" data-testid="pass-broker-skip-note">
                <MailX className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Es wird <b>keine Absage an den Broker</b> gesendet — der Deal wird nur intern gepasst.
              </div>
            )}
            {/* Stage sanity check (Talu, 12.10.2026): the pass is recorded from this stage — correct it here if needed */}
            {(() => {
              const list = PIPELINE_STAGES.filter(s => s.value !== 'passed');
              const cur = turnDownStage || project.pipeline_stage;
              const changed = cur !== project.pipeline_stage;
              return (
                <div className={`rounded-sm border px-2.5 py-2 space-y-1 ${changed ? 'border-sky-500/40 bg-sky-500/10' : 'border-border bg-secondary/30'}`} data-testid="pass-stage-check">
                  <p className="text-[11px] text-muted-foreground">
                    Der Deal steht aktuell in <b className="text-foreground">{(list.find(s => s.value === project.pipeline_stage) || {}).label || project.pipeline_stage}</b> — stimmt die Stage? Der Pass wird aus dieser Stage gezählt.
                  </p>
                  <select value={cur} onChange={e => setTurnDownStage(e.target.value)} data-testid="pass-stage-select"
                    className="w-full h-8 text-xs rounded-sm bg-secondary border border-border px-2 text-foreground focus:outline-none">
                    {list.map(s => <option key={s.value} value={s.value}>{s.label}{s.value === project.pipeline_stage ? ' (aktuell)' : ''}</option>)}
                  </select>
                  {changed && <p className="text-[10px] text-sky-300" data-testid="pass-stage-changed-note">Stage wird vor dem Pass auf „{list.find(s => s.value === cur)?.label}“ korrigiert.</p>}
                </div>
              );
            })()}
            <p className="text-sm text-muted-foreground">Why are you passing on this deal?</p>
            <PassReasonPicker selected={turnDownTags} onToggle={togglePassTag} />
            <Input autoFocus value={turnDownReason} onChange={e => setTurnDownReason(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && turnDownReason.trim()) handleTurnDown(); }}
              placeholder="e.g. Valuation too high, sector mismatch..." className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" data-testid="turn-down-reason-input" />
            <label className="flex items-start gap-2 cursor-pointer" data-testid="turn-down-bench-checkbox">
              <Checkbox checked={turnDownBench} onCheckedChange={(v) => setTurnDownBench(!!v)} className="mt-0.5" />
              <span className="text-sm text-foreground">
                Bench
                <span className="block text-[11px] text-muted-foreground">
                  The deal is passed, but kept searchable as "Benched" within the passed deals.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setTurnDownDialog(false)} className="text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleTurnDown} disabled={!turnDownReason.trim()} data-testid="turn-down-confirm-btn" className="bg-red-600 hover:bg-red-500 text-white rounded-sm font-medium text-xs h-8 px-3">Confirm Pass</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Brokered pass → broker decline flow (sends Absage mail; reason = internal pass reason) */}
      <DeclineDialog proposal={brokerProposal} open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onPassInternal={() => { setDeclineOpen(false); setPassBrokerSkip(true); setTurnDownDialog(true); }}
        onDone={() => { setDeclineOpen(false); toast.success('Deal abgesagt — Absage an den Broker gesendet'); refresh(); }} />

      <Dialog open={stageBackOpen} onOpenChange={setStageBackOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="stage-back-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-sky-400" /> Stage zurücksetzen
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Admin-Korrektur für versehentliches Weiterschieben: Der Deal wird ohne Gates in eine frühere Stage
            zurückgesetzt. Aktuell: <b className="text-foreground">{PIPELINE_STAGES.find(s => s.value === project.pipeline_stage)?.label || project.pipeline_stage}</b>
          </p>
          <Select value={stageBackTarget} onValueChange={setStageBackTarget}>
            <SelectTrigger className="bg-secondary border-border rounded-sm text-xs h-9" data-testid="stage-back-select">
              <SelectValue placeholder="Ziel-Stage wählen" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {PIPELINE_STAGES
                .slice(0, Math.max(0, PIPELINE_STAGES.findIndex(s => s.value === project.pipeline_stage)))
                .filter(s => s.value !== 'passed')
                .reverse()
                .map(s => (
                  <SelectItem key={s.value} value={s.value} className="text-xs" data-testid={`stage-back-option-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setStageBackOpen(false)} className="text-xs h-8 px-3">Abbrechen</Button>
            <Button disabled={stageBackSaving || !stageBackTarget} data-testid="stage-back-confirm-btn"
              className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm font-medium text-xs h-8 px-3"
              onClick={async () => {
                setStageBackSaving(true);
                try {
                  await api.stageBack(id, stageBackTarget);
                  toast.success('Stage zurückgesetzt');
                  setStageBackOpen(false);
                  await refresh();
                } catch (err) { toast.error(err.response?.data?.detail || 'Zurücksetzen fehlgeschlagen'); }
                setStageBackSaving(false);
              }}>
              {stageBackSaving ? 'Setze zurück…' : 'Zurücksetzen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={returnedDialog} onOpenChange={setReturnedDialog}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="letter-returned-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2"><MailWarning className="w-5 h-5 text-orange-400" /> Brief unzustellbar zurück</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Der Deal bleibt in „{STAGE_LABELS[project.pipeline_stage] || project.pipeline_stage}“, wird aber pausiert: Follow-up-Sequenz abgebrochen, offene Tasks storniert, keine neuen Tasks.
              Später „Reaktivieren“ → zurück nach Outreach WIP, Adresse korrigieren und den Brief erneut versenden.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={returnedNote} onChange={(e) => setReturnedNote(e.target.value)} rows={3} maxLength={500}
            placeholder="Notiz (optional) — z. B. „Empfänger unbekannt verzogen“" data-testid="letter-returned-note" className="text-sm" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReturnedDialog(false)} data-testid="letter-returned-cancel-btn">Abbrechen</Button>
            <Button disabled={returnedBusy} onClick={async () => {
              setReturnedBusy(true);
              try { const r = await api.letterReturned(id, returnedNote); toast.success(r.data.message); setReturnedDialog(false); await refresh(); }
              catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              setReturnedBusy(false);
            }} data-testid="letter-returned-confirm-btn" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm text-xs h-8">
              {returnedBusy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Als unzustellbar markieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hintDialog} onOpenChange={setHintDialog}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="meeting-hint-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" /> Meeting-Notiz
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Kurzer Hinweis fürs Team — wird auf der Agenda-Karte angezeigt, wenn der Deal im Team-Meeting besprochen wird.
          </p>
          <textarea value={hintText} onChange={e => setHintText(e.target.value)} maxLength={500} rows={3} autoFocus
            placeholder="z.B. Inhaber will nur bei Nachfolge intern verkaufen — Rückfrage zu Sohn im Betrieb"
            className="w-full bg-secondary border border-border rounded-sm p-2 text-sm text-foreground resize-none focus:outline-none focus:border-amber-500/50"
            data-testid="meeting-hint-input" />
          <div className="flex justify-end gap-2">
            {project.meeting_hint && (
              <Button variant="ghost" disabled={savingHint} className="text-xs h-8 px-3 text-red-400 hover:text-red-300"
                data-testid="meeting-hint-clear-btn"
                onClick={async () => {
                  setSavingHint(true);
                  try { await api.setMeetingHint(id, ''); toast.success('Meeting-Notiz entfernt'); setHintDialog(false); refresh(); }
                  catch { toast.error('Speichern fehlgeschlagen'); }
                  setSavingHint(false);
                }}>Entfernen</Button>
            )}
            <Button variant="ghost" onClick={() => setHintDialog(false)} className="text-xs h-8 px-3">Abbrechen</Button>
            <Button disabled={savingHint || !hintText.trim()} data-testid="meeting-hint-save-btn"
              className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm font-medium text-xs h-8 px-3"
              onClick={async () => {
                setSavingHint(true);
                try { await api.setMeetingHint(id, hintText.trim()); toast.success('Meeting-Notiz gespeichert'); setHintDialog(false); refresh(); }
                catch { toast.error('Speichern fehlgeschlagen'); }
                setSavingHint(false);
              }}>
              {savingHint ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Workflow: journey timeline + next-step tasks ── */}
      {!isAcMember && (
        <div id="section-workflow" className="scroll-mt-24 space-y-6">
          <DealTimeline projectId={id} stage={project.pipeline_stage} />
          <DealTasks projectId={id} stage={project.pipeline_stage} onStageChanged={refresh} />
        </div>
      )}

      {/* ── Analysis: Executive Summary ── */}
      <div id="section-summary" className="scroll-mt-24">
        <ExecutiveSummary projectId={id} project={project} documents={project.documents} screeningStatus={screenStatus.executive_summary} onScreenStarted={handleScreenStarted} companyWebsite={project.company_website} onRefresh={refresh} isAcMember={isAcMember} />
      </div>

      {(project.documents || []).length === 0 && !project.phase1 && !project.phase2 && (
        <div className="border border-border rounded-sm bg-card p-12 text-center">
          <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No documents uploaded yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Upload a Teaser or CIM to start screening</p>
          <Button onClick={() => setDocUploadOpen(true)} className="mt-4 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3"><Upload className="w-3.5 h-3.5 mr-1" /> Upload Document</Button>
        </div>
      )}

      {/* ── Process group: Analysis ── */}
      <ProcessSection id="group-analysis" testid="group-analysis" icon={BarChart3} iconClass="text-blue-400"
        title="Analysis" subtitle="Key financials · Sources & Uses · scorecards"
        open={!!openGroups?.analysis} onToggle={() => toggleGroup('analysis')}
        badges={<>
          {project.financials && <GroupChip>Financials ✓</GroupChip>}
          {project.phase1?.max_score > 0 && (
            <GroupChip tone="border-blue-500/40 bg-blue-500/10 text-blue-300">
              P1 {Math.round((project.phase1.total_score / project.phase1.max_score) * 100)}%
            </GroupChip>
          )}
          {project.phase2?.max_score > 0 && (
            <GroupChip tone="border-green-500/40 bg-green-500/10 text-green-300">
              P2 {Math.round((project.phase2.total_score / project.phase2.max_score) * 100)}%
            </GroupChip>
          )}
        </>}>
      <div id="section-financials" className="scroll-mt-24 space-y-6">
        <FinancialChart projectId={id} financials={project.financials} documents={project.documents} screeningStatus={screenStatus.financials} onScreenStarted={handleScreenStarted} companyWebsite={project.company_website} isAcMember={isAcMember} dealType={project.deal_type} />
        <SourcesUsesCalculator projectId={id} financials={project.financials} sourcesUses={project.sources_uses} isAcMember={isAcMember} investorAppetite={project.investor_appetite} />
      </div>

      {/* ── Analysis: Scorecards ── */}
      <div id="section-scorecards" className="scroll-mt-24 space-y-6">
      {/* Phase 1 Scorecard */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setExpandedCriteria(p => ({ ...p, '__phase1_open': !p.__phase1_open }))}
            aria-expanded={!!expandedCriteria.__phase1_open} data-testid="phase1-toggle"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left group flex-1">
            <div className={`w-8 h-8 rounded-sm flex items-center justify-center transition-colors ${expandedCriteria.__phase1_open ? 'bg-blue-500/20' : 'bg-secondary'}`}>
              <ChevronDown className={`w-4 h-4 text-blue-400 transition-transform duration-200 ${expandedCriteria.__phase1_open ? '' : '-rotate-90'}`} />
            </div>
            <div>
              <span className="font-heading text-base text-foreground">Phase 1 — K.O. Screen</span>
              {project.phase1 && <span className="text-xs text-muted-foreground font-mono ml-3">{project.phase1.total_score}/{project.phase1.max_score} ({Math.round((project.phase1.total_score / project.phase1.max_score) * 100)}%)</span>}
            </div>
          </button>
          {!isAcMember && project.deal_type === 'brokered' && (
          <ScreenDocButtons
            projectId={id}
            documents={project.documents}
            section="phase1"
            screening={screenStatus.phase1 === 'processing'}
            onStarted={handleScreenStarted}
            label="Phase 1 K.O."
            companyWebsite={project.company_website}
          />
          )}
        </div>
        {expandedCriteria.__phase1_open && project.phase1 && config && <div className="p-4 border-t border-border">{renderPhase('phase1')}</div>}
        {expandedCriteria.__phase1_open && !project.phase1 && (
          <div className="p-8 border-t border-border text-center">
            <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">No Phase 1 analysis yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Use "Screen Teaser" or "Screen CIM" above to run</p>
          </div>
        )}
      </div>

      {/* Phase 2 Scorecard */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setExpandedCriteria(p => ({ ...p, '__phase2_open': !p.__phase2_open }))}
            aria-expanded={!!expandedCriteria.__phase2_open} data-testid="phase2-toggle"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left group flex-1">
            <div className={`w-8 h-8 rounded-sm flex items-center justify-center transition-colors ${expandedCriteria.__phase2_open ? 'bg-green-500/20' : 'bg-secondary'}`}>
              <ChevronDown className={`w-4 h-4 text-green-400 transition-transform duration-200 ${expandedCriteria.__phase2_open ? '' : '-rotate-90'}`} />
            </div>
            <div>
              <span className="font-heading text-base text-foreground">Phase 2 — Deep Dive</span>
              {project.phase2 && <span className="text-xs text-muted-foreground font-mono ml-3">{project.phase2.total_score}/{project.phase2.max_score} ({Math.round((project.phase2.total_score / project.phase2.max_score) * 100)}%)</span>}
            </div>
          </button>
          {!isAcMember && project.deal_type === 'brokered' && (
          <ScreenDocButtons
            projectId={id}
            documents={project.documents}
            section="phase2"
            screening={screenStatus.phase2 === 'processing'}
            onStarted={handleScreenStarted}
            label="Phase 2 Deep Dive"
            companyWebsite={project.company_website}
          />
          )}
        </div>
        {expandedCriteria.__phase2_open && project.phase2 && config && <div className="p-4 border-t border-border">{renderPhase('phase2')}</div>}
        {expandedCriteria.__phase2_open && !project.phase2 && (
          <div className="p-8 border-t border-border text-center">
            <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">No Phase 2 analysis yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Use "Screen Teaser" or "Screen CIM" above to run</p>
          </div>
        )}
      </div>
      </div>
      </ProcessSection>

      {/* ── Process groups: Outreach & Engagement (brokered deals show Engagement first) ── */}
      {(() => {
        const outreachSection = !isAcMember ? (
          <ProcessSection key="group-outreach" id="group-outreach" testid="group-outreach" icon={Megaphone} iconClass="text-amber-400"
            title="Outreach" subtitle="Letter steps · scan & response tracking · sequences"
            open={!!openGroups?.outreach} onToggle={() => toggleGroup('outreach')}
            badges={<>
              {project.first_contact_date && (
                <GroupChip tone="border-emerald-500/40 bg-emerald-500/10 text-emerald-300">Contacted {project.first_contact_date}</GroupChip>
              )}
            </>}>
            <div id="section-outreach" className="scroll-mt-24 space-y-6">
              <OutreachStepsCard projectId={id} stage={project.pipeline_stage} onStageChanged={refresh} />
              <DealOutreachTracking projectId={id} />
              <DealSequencesCard projectId={id} />
            </div>
          </ProcessSection>
        ) : null;
        const engagementSection = (
          <ProcessSection key="group-engagement" id="group-engagement" testid="group-engagement" icon={Handshake} iconClass="text-teal-400"
            title="Engagement" subtitle="Contacts · emails · NDA · meetings & calls"
            open={!!openGroups?.engagement} onToggle={() => toggleGroup('engagement')}
            badges={<>
              {(project.documents || []).some(d => (d.label || '').toLowerCase().includes('nda')) && (
                <GroupChip tone="border-teal-500/40 bg-teal-500/10 text-teal-300">NDA ✓</GroupChip>
              )}
            </>}>
            <div id="section-meetings" className="scroll-mt-24 space-y-6">
              {!isAcMember && (
                <>
                  <CallGuideCard projectId={id} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <DealContactsCard projectId={id} companyName={project?.name || ''} />
                    <EmailCorrespondenceCard projectId={id} />
                  </div>
                  <DealLinkedContactsCard projectId={id} companyName={project?.name || ''} />
                  {project.deal_type === 'brokered' && <BrokerNdaReviewCard projectId={id} />}
                  <NdaCard projectId={id} project={project} isAdmin={isAdmin} isAcMember={isAcMember} onChanged={refresh} />
                  <OnsitePlannerCard projectId={id} />
                </>
              )}
              <MeetingNotes projectId={id} isAcMember={isAcMember} />
              {!isAcMember && (
                <div id="section-refcalls" className="scroll-mt-24">
                  <ReferenceCallsCard projectId={id} />
                </div>
              )}
            </div>
          </ProcessSection>
        );
        return project.deal_type === 'brokered'
          ? <>{engagementSection}{outreachSection}</>
          : <>{outreachSection}{engagementSection}</>;
      })()}

      {/* ── Process group: Collaboration ── */}
      <ProcessSection id="group-collab" testid="group-collab" icon={MessagesSquare} iconClass="text-violet-400"
        title="Collaboration" subtitle="Team discussion · AC feedback · AC key updates"
        open={!!openGroups?.collab} onToggle={() => toggleGroup('collab')}
        badges={<>
          {unreadMentions > 0 && (
            <GroupChip tone="border-red-600/40 bg-red-600/10 text-red-400">{unreadMentions} new @</GroupChip>
          )}
        </>}>
        <div id="section-discussion" className="scroll-mt-24 space-y-6">
          <DealComments projectId={id} />
          <DealACFeedback projectId={id} />
          {!isAcMember && <AcKeyUpdatesCard project={project} />}
        </div>
      </ProcessSection>

      {/* ── Process group: Records ── */}
      <ProcessSection id="group-records" testid="group-records" icon={FolderOpen} iconClass="text-sky-400"
        title="Records" subtitle="Documents · activity log · photos · Buy & Build"
        open={!!openGroups?.records} onToggle={() => toggleGroup('records')}
        badges={<>
          <GroupChip>{(project.documents || []).length} doc{(project.documents || []).length !== 1 ? 's' : ''}</GroupChip>
          {(project.photos || []).length > 0 && <GroupChip>{(project.photos || []).length} photo{(project.photos || []).length !== 1 ? 's' : ''}</GroupChip>}
        </>}>
      <div id="section-documents" className="scroll-mt-24 space-y-3">
        {showPdf && (
          <div className="border border-border rounded-sm overflow-hidden bg-card">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-secondary/30">
              <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Document</span><span className="text-xs text-muted-foreground">— {project.filename}</span></div>
              <Button variant="ghost" size="sm" onClick={() => setShowPdf(false)} className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></Button>
            </div>
            <iframe src={pdfBlobUrl ? `${pdfBlobUrl}#toolbar=1` : ''} title="PDF" className="w-full bg-muted" style={{ height: '600px' }} />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-base text-foreground">Documents ({(project.documents||[]).length})</p>
              {!isAcMember && <button onClick={() => setDocUploadOpen(true)} className="text-xs text-blue-400 hover:text-blue-300"><Upload className="w-3 h-3 inline mr-1" />Add</button>}
            </div>
            <div className="space-y-1.5">
              {(project.documents||[]).map((d, di) => (
                <button key={d.id} onClick={() => togglePdf(d.id)} data-testid={`deal-document-row-${di}`}
                  className="w-full flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-secondary/30 transition-colors text-left group">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground group-hover:text-blue-400 transition-colors" />
                    <span className="text-sm text-foreground group-hover:text-blue-400 transition-colors">{d.filename}</span>
                    <span className="text-[10px] text-muted-foreground border border-border rounded px-1">{d.label}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{new Date(d.uploaded_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})}</span>
                </button>
              ))}
              {(project.documents||[]).length === 0 && <p className="text-xs text-muted-foreground">No documents</p>}
            </div>
            {project.phase1_history?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Previous Analyses</p>
                {project.phase1_history.map((h,i) => (
                  <div key={h.analyzed_at || `hist-${i}`} className="text-xs text-muted-foreground py-0.5"><span className="font-mono">{h.total_score}/{h.max_score}</span> — {h.document_label||'Doc'} — {new Date(h.analyzed_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})}</div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <p className="font-heading text-base text-foreground mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-foreground/50" /> Activity
            </p>
            <DealActivityLog projectId={id} />
          </div>
        </div>
        <DealPhotosCard projectId={id} photos={project.photos || []} isAcMember={isAcMember} onChanged={refresh} />
      </div>
      <div id="section-links" className="scroll-mt-24">
        <DealLinks projectId={id} isAcMember={isAcMember} />
      </div>
      </ProcessSection>
    </div>
  );
}


function NegativeResponseBanner({ project, id, refresh }) {
  const p = project.negative_response_prompt || {};
  const [busy, setBusy] = useState('');

  const confirm = async () => {
    if (!window.confirm('Sequenz stoppen, alle offenen Tasks schließen und den Deal auf Passed setzen?')) return;
    setBusy('confirm');
    try {
      const r = await api.confirmNegativeResponse(id);
      toast.success(`Deal gepasst — ${r.data.sequences_cancelled} Sequenz(en) gestoppt, ${r.data.tasks_cancelled} Task(s) geschlossen`);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const dismiss = async () => {
    setBusy('dismiss');
    try {
      await api.dismissNegativeResponse(id);
      toast.success('Empfehlung verworfen — der Deal bleibt aktiv');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  return (
    <div className="bg-red-950/30 border border-red-800/40 rounded-sm p-4" data-testid="negative-response-banner">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <XCircle className="w-4 h-4 text-red-400" />
        <p className="text-sm font-medium text-red-400">KI hat eine Absage erkannt</p>
        {p.at && (
          <span className="text-[10px] text-muted-foreground font-mono">
            {new Date(p.at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}
          </span>
        )}
      </div>
      <div className="ml-6">
        <p className="text-sm text-foreground/80" data-testid="negative-response-summary">
          "{p.summary || 'Der Inhaber möchte nicht verkaufen / keinen weiteren Kontakt.'}"
        </p>
        {p.subject && <p className="text-[10px] text-muted-foreground mt-1">Betreff: {p.subject}{p.email ? ` · ${p.email}` : ''}</p>}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button size="sm" onClick={confirm} disabled={!!busy} data-testid="negative-response-confirm-btn"
            className="bg-red-700 hover:bg-red-600 text-white rounded-sm text-xs h-7">
            {busy === 'confirm' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
            Sequenz stoppen & Deal passen
          </Button>
          <Button size="sm" variant="outline" onClick={dismiss} disabled={!!busy}
            data-testid="negative-response-dismiss-btn"
            className="border-border text-muted-foreground rounded-sm text-xs h-7">
            {busy === 'dismiss' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Deal behalten
          </Button>
          <span className="text-[10px] text-muted-foreground">Bestätigung stoppt alle Sequenzen und schließt alle offenen Tasks.</span>
        </div>
      </div>
    </div>
  );
}

function PassedBanner({ project, id, isAdmin, refresh }) {
  const td = project.turned_down || {};
  const { user: me } = useAuth();
  const ownRecent = !!me && [td.by_id, td.confirmed_by_id].includes(me.id) && !!td.at
    && (Date.now() - new Date(td.at).getTime()) <= 7 * 86400000;
  const canReactivate = isAdmin || ownRecent;
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateStage, setReactivateStage] = useState(project.pre_pass_stage || 'succession_possible');
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivating, setReactivating] = useState(false);

  const handleReactivate = async () => {
    if (!reactivateReason.trim()) { toast.error('Bitte eine Begründung angeben'); return; }
    setReactivating(true);
    try {
      const res = await api.reactivateProject(id, reactivateStage, reactivateReason.trim());
      toast.success(res.data.message || 'Deal reaktiviert');
      setReactivateOpen(false); setReactivateReason('');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setReactivating(false); }
  };

  return (
    <div className="bg-red-950/30 border border-red-800/40 rounded-sm p-4" data-testid="passed-banner">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <XCircle className="w-4 h-4 text-red-400" />
        <p className="text-sm font-medium text-red-400">Deal Passed</p>
        {project.benched && (
          <span className="text-[9px] font-mono font-bold text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded px-1.5 py-0.5" data-testid="passed-benched-badge">BENCHED</span>
        )}
        <span className="text-[10px] text-muted-foreground font-mono">
          {td.by ? `by ${td.by}` : ''}
          {td.at ? ` — ${new Date(td.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
        </span>
        {canReactivate && (
          <Button onClick={() => setReactivateOpen(true)} size="sm" data-testid="reactivate-deal-btn"
            title={isAdmin ? 'Admin' : 'Eigenen Pass innerhalb von 7 Tagen zurücknehmen'}
            className="ml-auto bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300 border border-emerald-800/40 rounded-sm text-[10px] h-6 px-2.5">
            <Undo2 className="w-3 h-3 mr-1" /> Deal reaktivieren
          </Button>
        )}
      </div>
      <div className="ml-6">
        <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Reason for passing</p>
        <p className="text-sm text-foreground/80" data-testid="passed-reason">"{td.reason || 'No reason recorded'}"</p>
        {td.confirmed_by && <p className="text-[10px] text-muted-foreground mt-1.5">Confirmed by {td.confirmed_by}</p>}
        <p className="text-[11px] mt-1.5" data-testid="passed-bench-status">
          {project.benched
            ? <span className="text-amber-400">Benched — this deal stays searchable via "Passed (Benched)" in the Deal Explorer.</span>
            : <span className="text-muted-foreground">Not benched.</span>}
        </p>
      </div>

      <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="reactivate-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground text-sm flex items-center gap-2">
              <Undo2 className="w-4 h-4 text-emerald-400" /> Deal reaktivieren (Admin)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] text-muted-foreground">Ziel-Stage</Label>
              <Select value={reactivateStage} onValueChange={setReactivateStage}>
                <SelectTrigger className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs mt-1" data-testid="reactivate-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border rounded-sm">
                  {PIPELINE_STAGES.filter(s => s.value !== 'passed').map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-xs" data-testid={`reactivate-stage-option-${s.value}`}>
                      {s.label}{s.value === project.pre_pass_stage ? ' (Stage vor dem Pass)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Begründung (Pflicht)</Label>
              <textarea value={reactivateReason} onChange={(e) => setReactivateReason(e.target.value)}
                placeholder="Warum wird der Deal zurückgeholt?"
                data-testid="reactivate-reason-input"
                className="w-full mt-1 bg-secondary border border-border rounded-sm p-2 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-emerald-700/50 min-h-[70px]" />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Stage-Gates werden übersprungen. Die Reaktivierung wird in der Deal Journey protokolliert
              {td.by ? ` und ${td.by} (ursprünglicher Passer) wird benachrichtigt` : ''}.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setReactivateOpen(false)}
                className="text-muted-foreground rounded-sm text-xs h-7">Abbrechen</Button>
              <Button onClick={handleReactivate} disabled={reactivating || !reactivateReason.trim()} size="sm"
                data-testid="reactivate-confirm-btn"
                className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-7">
                {reactivating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Undo2 className="w-3 h-3 mr-1" />}
                Reaktivieren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PassProposedBanner({ project, id, isAcMember, currentUser, refresh }) {
  const [editingReason, setEditingReason] = useState(false);
  const [reasonText, setReasonText] = useState(project.pass_proposed.reason || '');
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const pp = project.pass_proposed;
  const perm = (project.review_permissions || {}).pass || {};
  const waitingOn = (perm.pending_from || []).map(u => u.name.split(' ')[0]).join(' oder ');
  const canConfirm = !isAcMember && currentUser && !!perm.can_confirm;

  const handleConfirm = async () => {
    try {
      // Update the reason if edited
      if (reasonText !== pp.reason) {
        await api.updatePassReason(id, reasonText);
      }
      const res = await api.confirmPass(id);
      toast.success(res.data.message);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleOverride = async () => {
    try {
      await api.overridePass(id, overrideReason.trim());
      toast.success('Pass overridden — deal stays active');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="bg-red-950/20 border border-red-800/30 rounded-sm p-4" data-testid="pass-proposed-banner">
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="w-4 h-4 text-red-400" />
        <p className="text-sm font-medium text-red-400">Pass Proposed</p>
        {pp.benched && (
          <span className="text-[9px] font-mono font-bold text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded px-1.5 py-0.5" data-testid="pass-benched-tag">BENCH</span>
        )}
        <span className="text-[10px] text-muted-foreground font-mono">
          by {pp.proposed_by} — {new Date(pp.proposed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          {pp.source === 'attio' && ' (via Attio)'}
        </span>
      </div>

      {/* Editable reason */}
      <div className="ml-6 mb-3">
        <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Reason for passing</p>
        {editingReason ? (
          <div className="flex gap-2">
            <textarea value={reasonText} onChange={e => setReasonText(e.target.value)}
              className="flex-1 bg-secondary border border-border rounded-sm p-2 text-sm text-foreground resize-none h-16 focus:outline-none focus:border-red-500"
              data-testid="edit-pass-reason" />
            <Button onClick={() => setEditingReason(false)} size="sm" variant="ghost" className="text-xs h-8 text-muted-foreground">Done</Button>
          </div>
        ) : (
          <div className="flex items-start gap-2 group">
            <p className="text-sm text-foreground/80 flex-1">"{reasonText || 'No reason given'}"</p>
            {canConfirm && (
              <button onClick={() => setEditingReason(true)} className="opacity-0 group-hover:opacity-100 text-muted-foreground/40 hover:text-foreground transition-all p-0.5">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {(pp.confirmations || []).length > 0 && (
        <p className="text-[10px] text-amber-400 ml-6 mb-2">
          Confirmed by: {pp.confirmations.map(c => c.name).join(', ')}
        </p>
      )}

      {/* Action buttons */}
      <div className="ml-6 flex items-center gap-2">
        {canConfirm && !showOverride && (
          <>
            <Button onClick={handleConfirm} data-testid="confirm-pass-btn"
              className="bg-red-600 hover:bg-red-500 text-white rounded-sm text-xs h-8 px-3">
              <Check className="w-3.5 h-3.5 mr-1" /> Confirm Pass
            </Button>
            <Button onClick={() => setShowOverride(true)}
              data-testid="override-pass-btn"
              variant="outline" className="border-emerald-600/30 text-emerald-400 hover:bg-emerald-600/10 rounded-sm text-xs h-8 px-3">
              Keep Active
            </Button>
          </>
        )}
        {!isAcMember && currentUser && pp.proposed_by_id === currentUser?.id && (
          <span className="text-[10px] text-muted-foreground italic" data-testid="pass-waiting-hint">
            {waitingOn ? `Wartet auf Bestätigung durch ${waitingOn}` : 'Awaiting confirmation from another team member'}
          </span>
        )}
        {!isAcMember && currentUser && pp.proposed_by_id !== currentUser?.id && !canConfirm && (
          <span className="text-[10px] text-muted-foreground italic" data-testid="pass-waiting-hint">
            {waitingOn ? `Wartet auf Bestätigung durch ${waitingOn}` : 'Wartet auf Bestätigung'}
          </span>
        )}
      </div>
      {canConfirm && showOverride && (
        <div className="ml-6 mt-2 space-y-2" data-testid="override-reason-form">
          <p className="text-[10px] text-emerald-400 uppercase tracking-wider">Why keep this deal active? (shared with {pp.proposed_by})</p>
          <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
            placeholder="Reasoning for reactivating the deal (optional but recommended)..."
            data-testid="override-reason-input"
            className="w-full bg-secondary border border-border rounded-sm p-2 text-sm text-foreground resize-none h-16 focus:outline-none focus:border-emerald-500" />
          <div className="flex items-center gap-2">
            <Button onClick={handleOverride} data-testid="override-confirm-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-3">
              <Check className="w-3.5 h-3.5 mr-1" /> Keep Active
            </Button>
            <Button onClick={() => { setShowOverride(false); setOverrideReason(''); }} variant="ghost"
              className="text-xs h-8 px-3 text-muted-foreground">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function DealActivityLog({ projectId }) {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.getDealActivity(projectId).then(res => {
      setEntries(res.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="text-xs text-muted-foreground py-4 text-center">Loading...</div>;
  if (entries.length === 0) return <p className="text-xs text-muted-foreground py-4 text-center">No activity yet</p>;

  return (
    <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar" data-testid="deal-activity-log">
      {entries.map((e, i) => (
        <div key={i} className="flex items-start gap-2 py-1">
          <div className="w-1 h-1 rounded-full bg-blue-400/40 mt-2 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-foreground/70 leading-snug">{e.detail}</p>
            <p className="text-[9px] text-muted-foreground/50 mt-0.5">
              {e.user_name} · {new Date(e.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// Sticky jump navigation for the deal page sections (desktop sticky, scrolls with page on mobile).
function SectionNav({ items, onJump }) {
  const [active, setActive] = useState(items[0]?.id);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-15% 0px -70% 0px' });
    items.forEach(it => { const el = document.getElementById(it.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [items]);
  const jump = (it) => {
    onJump?.(it);
    setTimeout(() => document.getElementById(it.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      it.group ? 250 : 0);
  };
  return (
    <div className="lg:sticky lg:top-0 z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 py-1.5 bg-background/95 backdrop-blur-sm border-b border-border/50 flex items-center gap-1 overflow-x-auto"
      data-testid="deal-section-nav">
      {items.map(it => (
        <button key={it.id} onClick={() => jump(it)} data-testid={`section-nav-${it.id}`}
          className={`shrink-0 inline-flex items-center text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active === it.id ? 'bg-blue-600/15 border-blue-500/40 text-blue-400 font-medium' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary'}`}>
          {it.label}
          {it.dot && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" data-testid={`section-nav-dot-${it.id}`} />}
        </button>
      ))}
    </div>
  );
}
